import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

type AdminUser = {
  _id?: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
};

const AdminUsers = () => {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [form, setForm] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
  });

  const fetchAdmins = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      const response = await axios.get('/users/admins/list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setAdmins(list.filter((item: AdminUser) => item.roles?.includes('store_admin')));
    } catch (err: any) {
      if (err?.response?.status === 404) {
        try {
          const fallbackResponse = await axios.get('/users/admins', {
            headers: { Authorization: `Bearer ${token}` },
          });
          const fallbackList = Array.isArray(fallbackResponse.data) ? fallbackResponse.data : [];
          setAdmins(fallbackList.filter((item: AdminUser) => item.roles?.includes('store_admin')));
          return;
        } catch (fallbackErr: any) {
          if (fallbackErr?.response?.status === 404) {
            try {
              const prefixedResponse = await axios.get('/api/v1/users/admins/list', {
                headers: { Authorization: `Bearer ${token}` },
              });
              const prefixedList = Array.isArray(prefixedResponse.data) ? prefixedResponse.data : [];
              setAdmins(prefixedList.filter((item: AdminUser) => item.roles?.includes('store_admin')));
              return;
            } catch (prefixedErr: any) {
              if (prefixedErr?.response?.status === 404) {
                try {
                  const prefixedAliasResponse = await axios.get('/api/v1/users/admins', {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const prefixedAliasList = Array.isArray(prefixedAliasResponse.data) ? prefixedAliasResponse.data : [];
                  setAdmins(prefixedAliasList.filter((item: AdminUser) => item.roles?.includes('store_admin')));
                  return;
                } catch (prefixedAliasErr: any) {
                  setError(prefixedAliasErr?.response?.data?.message || 'Failed to load admin users.');
                  return;
                }
              }

              setError(prefixedErr?.response?.data?.message || 'Failed to load admin users.');
              return;
            }
          }

          setError(fallbackErr?.response?.data?.message || 'Failed to load admin users.');
          return;
        }
      }

      setError(err?.response?.data?.message || 'Failed to load admin users.');
    }
  }, [token]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const resetForm = () => {
    setForm({ username: '', email: '', firstName: '', lastName: '', password: '' });
    setEditingAdmin(null);
  };

  const openCreateDialog = () => {
    setError('');
    setSuccess('');
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (admin: AdminUser) => {
    setError('');
    setSuccess('');
    setEditingAdmin(admin);
    setForm({
      username: admin.username || '',
      email: admin.email || '',
      firstName: admin.firstName || '',
      lastName: admin.lastName || '',
      password: '',
    });
    setShowDialog(true);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!form.username || !form.email || !form.firstName || !form.lastName) {
      setError('All fields are required.');
      return;
    }

    try {
      if (editingAdmin?._id) {
        const payload: Record<string, string> = {
          username: form.username,
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
        };
        if (form.password) {
          payload.password = form.password;
        }

        await axios.put(`/users/admins/${editingAdmin._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Admin user updated successfully.');
      } else {
        if (!form.password) {
          setError('Password is required for a new admin.');
          return;
        }

        await axios.post('/users/admins', form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Admin user created successfully.');
      }

      setShowDialog(false);
      resetForm();
      await fetchAdmins();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save admin user.');
    }
  };

  const handleDelete = async (admin: AdminUser) => {
    if (!admin._id) {
      return;
    }

    if (!window.confirm(`Delete admin ${admin.username}?`)) {
      return;
    }

    setError('');
    setSuccess('');
    try {
      await axios.delete(`/users/admins/${admin._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Admin user deleted successfully.');
      await fetchAdmins();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete admin user.');
    }
  };

  return (
    <>
      <Card className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Store Admins</h4>
          <Button variant="dark" onClick={openCreateDialog}>Add Admin</Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center">No admin users found.</td>
              </tr>
            ) : (
              admins.map((admin) => (
                <tr key={admin._id ?? admin.email}>
                  <td>{admin.username}</td>
                  <td>{admin.email}</td>
                  <td>{`${admin.firstName ?? ''} ${admin.lastName ?? ''}`.trim()}</td>
                  <td>
                    <Button size="sm" variant="warning" className="me-2" onClick={() => openEditDialog(admin)}>Edit</Button>
                    <Button size="sm" variant="danger" onClick={() => handleDelete(admin)}>Delete</Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      <Modal show={showDialog} onHide={() => { setShowDialog(false); resetForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>{editingAdmin ? 'Edit Admin' : 'Add New Admin'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={onSubmit}>
            <Form.Group className="mb-2">
              <Form.Label>Username</Form.Label>
              <Form.Control value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>First Name</Form.Label>
              <Form.Control value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Last Name</Form.Label>
              <Form.Control value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{editingAdmin ? 'New Password (optional)' : 'Temporary Password'}</Form.Label>
              <Form.Control type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editingAdmin} />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" variant="dark">{editingAdmin ? 'Update Admin' : 'Create Admin'}</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AdminUsers;
